/**
 * SensitiveField Component
 * 
 * Displays field values with sensitivity-aware masking.
 * PHI and Sensitive fields are masked by default with click-to-reveal.
 * All reveal actions are logged for compliance auditing.
 */

import React, { useState, useCallback } from 'react';
import {
  SensitivityLevel,
  getFieldSensitivityLevel,
  SENSITIVITY_DISPLAY,
} from '@/app/config/sensitiveFields';
import { auditService } from '@/app/services/auditService';
import { logger } from '@/app/utils/logger';
import './SensitiveField.scss';

export interface SensitiveFieldProps {
  /** The field name - used for registry lookup if sensitivity not provided */
  fieldName: string;
  /** The actual value to display */
  value: React.ReactNode;
  /** Optional label to display */
  label?: string;
  /** Override sensitivity level (takes precedence over registry) */
  sensitivity?: SensitivityLevel;
  /** Custom mask text (default: "●●● Protected ●●●") */
  maskText?: string;
  /** Context for audit logging (e.g., transaction_id, user_id) */
  auditContext?: Record<string, string | number>;
  /** Resource type for audit logging (e.g., 'transaction', 'user') */
  resourceType?: string;
  /** Resource ID for audit logging */
  resourceId?: string;
  /** Callback when field is revealed (for custom audit logging) */
  onReveal?: (fieldName: string, auditContext?: Record<string, string | number>) => void;
  /** If true, field is always shown (for users with elevated permissions) */
  forceShow?: boolean;
  /** Show sensitivity indicator badge */
  showIndicator?: boolean;
  /** Skip confirmation dialog (default: false) */
  skipConfirmation?: boolean;
}

/**
 * Log audit event using the audit service
 */
const logAuditEvent = async (
  action: 'reveal_requested' | 'reveal_confirmed' | 'revealed' | 'hidden',
  fieldName: string,
  sensitivityLevel: SensitivityLevel,
  resourceType: string,
  resourceId?: string,
  metadata?: Record<string, unknown>
): Promise<void> => {
  try {
    await auditService.logSensitiveFieldAccess(
      action,
      fieldName,
      sensitivityLevel,
      resourceType,
      resourceId,
      metadata
    );
  } catch (error) {
    // Log error but don't block UI
    logger.error('[AUDIT] Failed to log sensitive field access', { error });
  }
};

/**
 * PHI Confirmation Dialog
 */
interface ConfirmationDialogProps {
  isOpen: boolean;
  sensitivityLevel: SensitivityLevel;
  fieldName: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  sensitivityLevel,
  fieldName: _fieldName, // Intentionally unused - we don't expose field names for security
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null;

  const isPHI = sensitivityLevel === 'phi';

  return (
    <div className="phi-confirmation-overlay" onClick={onCancel}>
      <div className="phi-confirmation-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="phi-confirmation-header">
          <span className="phi-confirmation-icon">{isPHI ? '🏥' : '⚠️'}</span>
          <h3>{isPHI ? 'Protected Health Information' : 'Sensitive Data'}</h3>
        </div>
        
        <div className="phi-confirmation-body">
          {isPHI ? (
            <>
              <p className="phi-warning">
                You are about to view <strong>Protected Health Information (PHI)</strong>.
              </p>
              <p>
                This data is protected under <strong>HIPAA regulations</strong> and should only be 
                accessed when necessary for your job function.
              </p>
            </>
          ) : (
            <>
              <p className="phi-warning">
                You are about to view <strong>sensitive information</strong>.
              </p>
              <p>
                This data may contain confidential details that should be handled with care.
              </p>
            </>
          )}
          
          <div className="phi-audit-notice">
            <span className="audit-icon">📋</span>
            <div>
              <strong>Audit Notice</strong>
              <p>
                Your access to this {isPHI ? 'PHI field' : 'field'} will be recorded 
                for compliance and auditing purposes, including your user ID and timestamp.
              </p>
            </div>
          </div>
        </div>

        <div className="phi-confirmation-footer">
          <button className="btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn-confirm" onClick={onConfirm}>
            I Understand, Show Data
          </button>
        </div>
      </div>
    </div>
  );
};

export const SensitiveField: React.FC<SensitiveFieldProps> = ({
  fieldName,
  value,
  label,
  sensitivity,
  maskText,
  auditContext,
  resourceType = 'unknown',
  resourceId,
  onReveal,
  forceShow = false,
  showIndicator = true,
  skipConfirmation = false,
}) => {
  // Determine sensitivity level (prop override > registry > default)
  const sensitivityLevel = sensitivity ?? getFieldSensitivityLevel(fieldName, 'public');
  const displayConfig = SENSITIVITY_DISPLAY[sensitivityLevel];
  
  // Should this field be masked?
  const shouldMask = !forceShow && (sensitivityLevel === 'phi' || sensitivityLevel === 'sensitive');
  
  // Track revealed state and dialog state
  const [isRevealed, setIsRevealed] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  
  // Handle reveal button click - show confirmation first
  const handleRevealClick = useCallback(() => {
    logAuditEvent('reveal_requested', fieldName, sensitivityLevel, resourceType, resourceId, auditContext);
    
    if (skipConfirmation) {
      // Skip confirmation, directly reveal
      logAuditEvent('revealed', fieldName, sensitivityLevel, resourceType, resourceId, auditContext);
      onReveal?.(fieldName, auditContext);
      setIsRevealed(true);
    } else {
      setShowConfirmDialog(true);
    }
  }, [fieldName, sensitivityLevel, resourceType, resourceId, auditContext, onReveal, skipConfirmation]);
  
  // Handle confirmation
  const handleConfirm = useCallback(() => {
    logAuditEvent('reveal_confirmed', fieldName, sensitivityLevel, resourceType, resourceId, auditContext);
    logAuditEvent('revealed', fieldName, sensitivityLevel, resourceType, resourceId, auditContext);
    onReveal?.(fieldName, auditContext);
    setIsRevealed(true);
    setShowConfirmDialog(false);
  }, [fieldName, sensitivityLevel, resourceType, resourceId, auditContext, onReveal]);
  
  // Handle cancel
  const handleCancel = useCallback(() => {
    setShowConfirmDialog(false);
  }, []);
  
  // Handle hide click
  const handleHide = useCallback(() => {
    logAuditEvent('hidden', fieldName, sensitivityLevel, resourceType, resourceId, auditContext);
    setIsRevealed(false);
  }, [fieldName, sensitivityLevel, resourceType, resourceId, auditContext]);
  
  // Determine what to display
  const displayValue = shouldMask && !isRevealed
    ? maskText ?? '●●● Protected ●●●'
    : value;
  
  // Don't render anything special for public fields
  if (sensitivityLevel === 'public' && !showIndicator) {
    return <>{value}</>;
  }
  
  return (
    <>
      <div className={`sensitive-field sensitive-field--${sensitivityLevel}`}>
        {label && (
          <span className="sensitive-field__label">
            {label}
            {showIndicator && sensitivityLevel !== 'public' && (
              <span 
                className="sensitive-field__indicator"
                title={displayConfig.description}
              >
                {displayConfig.icon}
              </span>
            )}
          </span>
        )}
        
        <div className="sensitive-field__content">
          <span className={`sensitive-field__value ${shouldMask && !isRevealed ? 'masked' : ''}`}>
            {displayValue}
          </span>
          
          {shouldMask && (
            <button
              type="button"
              className="sensitive-field__toggle"
              onClick={isRevealed ? handleHide : handleRevealClick}
              title={isRevealed ? 'Hide sensitive data' : 'Reveal sensitive data (logged)'}
            >
              {isRevealed ? '👁️ Hide' : '👁️ Show'}
            </button>
          )}
        </div>
      </div>
      
      <ConfirmationDialog
        isOpen={showConfirmDialog}
        sensitivityLevel={sensitivityLevel}
        fieldName={fieldName}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  );
};

/**
 * Inline variant for use within DataList or tables
 */
export const SensitiveValue: React.FC<Omit<SensitiveFieldProps, 'label' | 'showIndicator'>> = (props) => {
  const { 
    fieldName, 
    value, 
    sensitivity, 
    forceShow, 
    auditContext, 
    resourceType = 'unknown',
    resourceId,
    onReveal, 
    maskText, 
    skipConfirmation = false 
  } = props;
  
  const sensitivityLevel = sensitivity ?? getFieldSensitivityLevel(fieldName, 'public');
  const shouldMask = !forceShow && (sensitivityLevel === 'phi' || sensitivityLevel === 'sensitive');
  
  const [isRevealed, setIsRevealed] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  
  // Handle reveal button click - show confirmation first
  const handleRevealClick = useCallback(() => {
    logAuditEvent('reveal_requested', fieldName, sensitivityLevel, resourceType, resourceId, auditContext);
    
    if (skipConfirmation) {
      logAuditEvent('revealed', fieldName, sensitivityLevel, resourceType, resourceId, auditContext);
      onReveal?.(fieldName, auditContext);
      setIsRevealed(true);
    } else {
      setShowConfirmDialog(true);
    }
  }, [fieldName, sensitivityLevel, resourceType, resourceId, auditContext, onReveal, skipConfirmation]);
  
  // Handle confirmation
  const handleConfirm = useCallback(() => {
    logAuditEvent('reveal_confirmed', fieldName, sensitivityLevel, resourceType, resourceId, auditContext);
    logAuditEvent('revealed', fieldName, sensitivityLevel, resourceType, resourceId, auditContext);
    onReveal?.(fieldName, auditContext);
    setIsRevealed(true);
    setShowConfirmDialog(false);
  }, [fieldName, sensitivityLevel, resourceType, resourceId, auditContext, onReveal]);
  
  // Handle cancel
  const handleCancel = useCallback(() => {
    setShowConfirmDialog(false);
  }, []);
  
  const handleHide = useCallback(() => {
    logAuditEvent('hidden', fieldName, sensitivityLevel, resourceType, resourceId, auditContext);
    setIsRevealed(false);
  }, [fieldName, sensitivityLevel, resourceType, resourceId, auditContext]);
  
  if (!shouldMask) {
    return <>{value}</>;
  }
  
  const displayValue = isRevealed ? value : (maskText ?? '●●● Protected ●●●');
  const displayConfig = SENSITIVITY_DISPLAY[sensitivityLevel];
  
  return (
    <>
      <span className={`sensitive-value sensitive-value--${sensitivityLevel}`}>
        <span className={`sensitive-value__text ${!isRevealed ? 'masked' : ''}`}>
          {displayValue}
        </span>
        <button
          type="button"
          className="sensitive-value__toggle"
          onClick={isRevealed ? handleHide : handleRevealClick}
          title={`${displayConfig.icon} ${isRevealed ? 'Hide' : 'Reveal (logged)'}`}
        >
          {isRevealed ? '🙈' : '👁️'}
        </button>
      </span>
      
      <ConfirmationDialog
        isOpen={showConfirmDialog}
        sensitivityLevel={sensitivityLevel}
        fieldName={fieldName}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  );
};

export default SensitiveField;

